<i18n locale="en">
{
  "provider-id": {
    "label": "Provider ID",
    "placeholder": "This is automatically detected"
  },
  "embed-code": {
    "label": "OpenTable Widget Code",
    "helper": "Copy and paste the embed code from OpenTable and we'll detect the provider ID.",
    "placeholder": "Paste code here"
  },
  "validation": {
    "required": "This field is required",
    "invalid-code": "Invalid code snippet",
    "invalid-url": "Code snippet must be a valid OpenTable code snippet",
    "invalid-provider-id": "No provider ID detected"
  }
}
</i18n>

<template>
  <div class="flex-col gap-8">
    <OwnInputContainer
      :label="t('embed-code.label')"
      :helper-text="t('embed-code.helper')"
      :errors="mapErrorMessage(formState.embedCode.$errors)"
      :value="embedCode"
    >
      <OwnTextarea
        v-model="embedCode"
        rows="4"
        :placeholder="t('embed-code.placeholder')"
        @update:model-value="onEmbedCodeUpdate"
      />
    </OwnInputContainer>

    <OwnInputContainer :label="t('provider-id.label')" :value="providerId">
      <OwnInput
        v-model="providerId"
        disabled
        :placeholder="t('provider-id.placeholder')"
      />
    </OwnInputContainer>
  </div>
</template>

<script lang="ts" setup>
import { useVuelidate } from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnTextarea } from '@/ui/OwnTextarea'
import { mapErrorMessage } from '@/utils/vuelidate'

import type { ReservationEmbedSection } from '../../../WebsiteBuilder.types'
import { ReservationEmbedCommands } from '../commands/ReservationEmbedCommands'

const props = defineProps<{
  id: string
}>()

const { t } = useI18n()
const store = useStore()

const providerId = ref('')
const embedCode = ref('')

const isValidScriptTag = (value: string) => {
  const hasOpeningTag = value.includes('<script')
  const hasClosingTag = value.includes('/script>')
  return hasOpeningTag && hasClosingTag
}

const isValidURL = (value: string) => {
  const isOpenTableUrl = value.includes('opentable.com')
  return isOpenTableUrl
}

const extractProviderId = (embedCodeValue: string): string | null => {
  const ridMatch = embedCodeValue.match(/rid=([^&]+)/)
  return ridMatch && ridMatch[1] ? ridMatch[1] : null
}

const hasValidProviderId = (embedCodeValue: string) => {
  const providerId = extractProviderId(embedCodeValue)
  return providerId !== null
}

const rules = {
  embedCode: {
    required: helpers.withMessage(t('validation.required'), required),
    validProviderId: helpers.withMessage(
      t('validation.invalid-provider-id'),
      hasValidProviderId
    ),
    validScript: helpers.withMessage(
      t('validation.invalid-code'),
      isValidScriptTag
    ),
    validURL: helpers.withMessage(t('validation.invalid-url'), isValidURL),
  },
}

const formState = useVuelidate(rules, { embedCode })

const currentBlockParams = computed(() => {
  return store.getters['websiteBuilder/blockById'](props.id)
    ?.params as ReservationEmbedSection['params']
})

const onEmbedCodeUpdate = async (value: string) => {
  embedCode.value = value
  const isValid = await formState.value.$validate()

  if (!isValid) return
  const extractedProviderId = extractProviderId(value)

  if (!extractedProviderId) return

  setProviderId(extractedProviderId)
}

const setProviderId = async (value: string) => {
  providerId.value = value

  await ReservationEmbedCommands.updateBlock({
    targetId: props.id,
    update: {
      params: {
        providerId: value,
        providerType: 'opentable',
      },
    },
  })
}

onMounted(() => {
  providerId.value = currentBlockParams.value?.providerId ?? ''
})
</script>
